import React from 'react';
import RenderImg from '@op/components/RenderImg';
import { DBO_TM } from '@op/utils/format';
import { DefaultImg } from '@op/constants/defaultImg';
import classNames from 'classnames';
import './index.scss';

export interface IMediaCoverProps {
  /** 封面图 URL */
  coverUrl: string;
  /** 默认占位图，默认 DefaultImg.VIDEO */
  defaultImage?: string;
  /** 视频时长（秒），传入后显示时长角标 */
  duration?: number | string;
  /** 是否显示时长角标，默认 true */
  showDuration?: boolean;
  /** 容器自定义 className */
  className?: string;
  /** 封面图自定义 className */
  coverClassName?: string;
  /** 时长角标自定义 className */
  durationClassName?: string;
  /** 封面渲染模式，默认 'image' */
  mode?: 'image' | 'backgroundImage';
  /** 封面图额外样式 */
  coverStyle?: React.CSSProperties;
  /** 封面图点击事件 */
  onClick?: () => void;
  /** 子元素（覆盖层：悬停分享图标、播放按钮等） */
  children?: React.ReactNode;
}

/**
 * MediaCover - 通用封面组件
 *
 * 封装 "封面图 + 时长角标" 的组合模式，同时支持通过 children 传入其他覆盖层。
 *
 * @example 基础用法
 * ```tsx
 * <MediaCover
 *   coverUrl={item.cover}
 *   duration={item.duration}
 *   className="dbom-videobox-coverbox"
 *   coverClassName="dbom-videobox-cover"
 *   durationClassName="dbom-videobox-time"
 * />
 * ```
 *
 * @example 带分享图标覆盖层
 * ```tsx
 * <MediaCover coverUrl={url} duration={dur} className="dbom-adbox-coverbox">
 *   <div className="dbom-adbox-cover-share" onClick={handleShare}>
 *     <SvgIcon type="other-share-icon" />
 *   </div>
 * </MediaCover>
 * ```
 */
export default function MediaCover({
  coverUrl,
  defaultImage = DefaultImg.VIDEO,
  duration,
  showDuration = true,
  className,
  coverClassName,
  durationClassName,
  mode = 'backgroundImage',
  coverStyle,
  onClick,
  children,
}: IMediaCoverProps) {
  const hasDuration = showDuration && !!Number(duration);
  return (
    <div className={classNames('dbom-mediacover', className)}>
      <RenderImg
        coverImage={coverUrl}
        defaultImage={defaultImage}
        className={classNames('dbom-mediacover-img', coverClassName)}
        mode={mode}
        style={coverStyle}
        onClick={onClick}
      />
      {hasDuration && (
        <span
          className={classNames('dbom-mediacover-duration', durationClassName)}
        >
          {typeof duration === 'number'
            ? DBO_TM.duration(duration)
            : String(duration)}
        </span>
      )}
      {children}
    </div>
  );
}
